// react
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// components
import { ClientReferralEmailPreviewPage } from '@karehero/llama/pwa/pages/ClientReferralEmailPreviewPage/ClientReferralEmailPreviewPage';

// hooks
import { useGoBack } from 'hooks/useGoBack';

// store
import { useSendInternalReferralMutation } from 'store/api/referral';
import { useGetInviteEmailTemplateQuery } from 'store/api/organization';
import { useSelector } from 'store';
import { getAccount } from 'store/slices/iamSlice';
import {
  getClientData,
  getLeadData,
  resetClientReferral,
} from 'store/slices/clientReferralSlice';
import { newToast } from 'store/slices/uiSlice';

// router
import { ROUTES } from 'router';

// models
import { Referral } from '@karehero/models';

/**
 * ClientReferralReview wires the ClientReferralEmailPreviewPage to the referral API
 */
export const ClientReferralReview = () => {
  const navigate = useNavigate();
  const goBack = useGoBack();
  const dispatch = useDispatch();
  const account = useSelector(getAccount);
  const clientData = useSelector(getClientData);
  const leadData = useSelector(getLeadData);
  const [sendInternalReferral, { isLoading }] =
    useSendInternalReferralMutation();
  const { data: emailHtml } = useGetInviteEmailTemplateQuery();

  const gdprSelections = [
    leadData?.consentCommunications && 'communications',
    leadData?.consentShareInfo && 'shareInfo',
  ]
    .filter(Boolean)
    .join(',');

  const onSend = useCallback(
    async (personalizedMessage: string) => {
      if (!clientData || !account) return;

      const referral: Referral = {
        referrer: {
          firstName: account.firstName,
          lastName: account.lastName,
          email: account.email,
          partnerCode: account.partnerCode || '',
          referLtcCode: account.referLtcCode || '',
        },
        referral: {
          firstName: clientData.clientFirstName,
          lastName: clientData.clientLastName,
          email: clientData.clientEmail,
          phone: clientData.clientPhone,
          personReceivingCare:
            clientData.personReceivingCare === 'someone_else'
              ? `${clientData.careRecipientFirstName} ${clientData.careRecipientLastName}`.trim()
              : `${clientData.clientFirstName} ${clientData.clientLastName}`.trim(),
          careSituationContext: clientData.context,
          discountCode: leadData?.discountCode || '',
          licenceCode: leadData?.licenceCode || '',
          feePaid: leadData?.whoWillPay || '',
          personalizedMsg: personalizedMessage,
          gdprSelections,
        },
      };

      const res = await sendInternalReferral(referral);
      if ('error' in res) {
        dispatch(
          newToast({
            title: 'Error',
            description: 'Failed to send client referral.',
          }),
        );
        return;
      }

      navigate(ROUTES.PORTAL_CLIENT_REFERRAL_SENT, {
        replace: true,
        state: {
          firstName: clientData.clientFirstName,
          lastName: clientData.clientLastName,
          email: clientData.clientEmail,
        },
      });
      dispatch(resetClientReferral());
    },
    [
      clientData,
      leadData,
      account,
      gdprSelections,
      sendInternalReferral,
      dispatch,
      navigate,
    ],
  );

  const subject =
    `${clientData?.clientFirstName || ''} ${clientData?.clientLastName || ''} here's your invite from ${account?.firstName || ''} ${account?.lastName || ''} to use KareHero: Care Concierge in your Pocket`.trim();

  return (
    <ClientReferralEmailPreviewPage
      onBack={goBack}
      clientFirstName={clientData?.clientFirstName || ''}
      inviterName={`${account?.firstName || ''} ${account?.lastName || ''}`.trim()}
      recipientEmail={clientData?.clientEmail || ''}
      subject={subject}
      emailHtml={emailHtml || ''}
      onSend={onSend}
      isSending={isLoading}
    />
  );
};

export default ClientReferralReview;
